/**
 * Auto-recovery for stale clients that fail to lazy-load a chunk.
 *
 * Hashed chunks are immutable-cached, but a newer deploy can remove or rename
 * the chunk a stale tab still references. When such a tab navigates to a route
 * whose lazy chunk no longer exists, the dynamic import 404s — Vite's
 * `__vitePreload` helper dispatches a `vite:preloadError` window event (and,
 * unless defaultPrevented, rethrows into an unhandled rejection → white screen
 * via the ErrorBoundary).
 *
 * We reload once to fetch the current index.html (no-cache) and its fresh chunk
 * map. A sessionStorage timestamp guards against a reload loop: if a preload
 * error recurs within the cooldown, the reload clearly did not help, so we let
 * it surface to the ErrorBoundary instead of looping.
 */
const RELOAD_MARK_KEY = 'chunk-reload-at'
const RELOAD_COOLDOWN_MS = 10_000

export function registerChunkReloadOnPreloadError(): void {
  if (typeof window === 'undefined') return

  window.addEventListener('vite:preloadError', (event) => {
    let lastReloadAt = 0
    try {
      lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_MARK_KEY)) || 0
    } catch {
      // sessionStorage can throw (private mode / disabled). Treat as never-reloaded.
    }

    // A recent reload didn't fix it — let the error surface rather than loop.
    if (Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) return

    try {
      window.sessionStorage.setItem(RELOAD_MARK_KEY, String(Date.now()))
    } catch {
      // Ignore — the reload still helps even if we can't record the mark.
    }

    // Suppress the default rethrow so the console/ErrorBoundary doesn't flash
    // an error in the instant before the reload tears the document down.
    event.preventDefault()
    window.location.reload()
  })
}
